import { useState, useEffect, useRef } from 'react'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { useBackfillCompletion } from '../../BackfillContext'
import { useCommunityConfig } from '../../CommunityConfig'
import {
  isBackfillLoading,
  getBackfillStartTime,
} from '../../util/backfillCookies'
import { getIsRealtimeSync } from '../../util/channelSyncHelpers'

export const UseBackfillLoadingBanner_Channel = graphql(`
  fragment UseBackfillLoadingBanner_Channel on Channel @_unmask {
    id
    service
    type
  }
`)

type UseBackfillLoadingBannerProps = {
  channel: FragmentOf<typeof UseBackfillLoadingBanner_Channel> | undefined
}

/**
 * Hook that manages the visibility of the backfill loading banner.
 * Shows banner if cookie exists, hides it when cookie is cleared by Pusher events.
 * Shows error toast if cookie expires without receiving Pusher event (tracked via context).
 *
 * Note: Pusher events are handled globally in CommentsRouteHandler via useCommentsBackfill.
 * This hook polls cookies and uses context to distinguish timeout from successful completion.
 */
export function useBackfillLoadingBanner({
  channel,
}: UseBackfillLoadingBannerProps): {
  showBanner: boolean
  elapsedSeconds?: number
} {
  const channelId = channel?.id?.toString() ?? ''
  const [showBanner, setShowBanner] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState<number | undefined>(
    undefined,
  )
  const wasShowingBannerRef = useRef(false)
  const wasCookiePresentRef = useRef(false)
  const completionCyclesRef = useRef(0)
  const previousChannelIdRef = useRef<string | undefined>(undefined)
  const hideCompletionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const {
    hasChannelCompleted,
    getChannelCompletionMessage,
    clearChannelCompleted,
    hasChannelInitiated,
    clearChannelInitiated,
  } = useBackfillCompletion()
  const { config } = useCommunityConfig()

  // Reset state only when channel changes (separate effect with minimal deps)
  useEffect(() => {
    if (previousChannelIdRef.current !== channelId) {
      if (hideCompletionTimeoutRef.current) {
        clearTimeout(hideCompletionTimeoutRef.current)
        hideCompletionTimeoutRef.current = null
      }
      wasShowingBannerRef.current = false
      wasCookiePresentRef.current = false
      completionCyclesRef.current = 0
      previousChannelIdRef.current = channelId
    }
  }, [channelId])

  // Poll cookie status and update banner visibility
  useEffect(() => {
    if (!channelId || getIsRealtimeSync(channel, config)) {
      setShowBanner(false)
      return
    }

    const checkCookieStatus = (): void => {
      // Check if this channel has had a forced backfill initiated or completed
      const isInitiated = hasChannelInitiated(channelId)
      const isCompleted = hasChannelCompleted(channelId)

      // Check cookie first - it's the source of truth for "is backfill running"
      const cookieExists = isBackfillLoading(channelId)

      // Only proceed if:
      // 1. Channel has been initiated (forced backfill was triggered)
      // 2. Channel is showing completion message
      // 3. Cookie exists (handles page reload during active backfill)
      if (!isInitiated && !isCompleted && !cookieExists) {
        setShowBanner(false)
        return
      }
      const wasShowing = wasShowingBannerRef.current
      const wasCookiePresent = wasCookiePresentRef.current
      const completedViaContext = hasChannelCompleted(channelId)

      // Calculate elapsed time if backfill is in progress
      if (cookieExists) {
        const startTime = getBackfillStartTime(channelId)
        if (startTime) {
          const elapsed = Math.floor((Date.now() - startTime) / 1000) // Convert to seconds
          setElapsedSeconds(elapsed)
        }
      } else {
        setElapsedSeconds(undefined)
      }

      // Show error toast if cookie expired without Pusher event (not in completed set)
      // Only check if there was actually a cookie present (not just banner showing due to initiated state)
      if (wasCookiePresent && !cookieExists && !completedViaContext) {
        toast.error(
          'Sync took longer than expected. Please try syncing again.',
          { duration: 4_000 },
        )
        clearChannelInitiated(channelId)
        setShowBanner(false)
      }

      // Determine if banner should be showing
      let shouldShowBanner = false

      // If just completed, check if it's a success (has message) or error (empty message)
      if (wasShowing && !cookieExists && completedViaContext) {
        const completionMessage = getChannelCompletionMessage(channelId)

        // Only show success state if there's a completion message (not an error)
        if (completionMessage) {
          shouldShowBanner = true
          // Hide banner after 1 second
          if (hideCompletionTimeoutRef.current) {
            clearTimeout(hideCompletionTimeoutRef.current)
          }
          hideCompletionTimeoutRef.current = setTimeout(() => {
            setShowBanner(false)
            clearChannelCompleted(channelId)
            hideCompletionTimeoutRef.current = null
          }, 1_000)
        } else {
          // Error case - hide banner immediately
          shouldShowBanner = false
          completionCyclesRef.current = 0
        }
      } else if (completionCyclesRef.current > 0) {
        // Keep banner visible while timer is running
        shouldShowBanner = true
      } else {
        // Update banner visibility based on cookie or initiated state
        shouldShowBanner = cookieExists || isInitiated
      }

      setShowBanner(shouldShowBanner)

      // Track both states separately:
      // 1. Whether banner was showing (for completion message detection)
      // 2. Whether cookie was present (for timeout toast detection)
      wasShowingBannerRef.current = shouldShowBanner
      wasCookiePresentRef.current = cookieExists
    }

    // Run once immediately and then set up the interval with 3 second polling
    checkCookieStatus()
    const intervalId = setInterval(checkCookieStatus, 3_000)

    return (): void => {
      clearInterval(intervalId)
      if (hideCompletionTimeoutRef.current) {
        clearTimeout(hideCompletionTimeoutRef.current)
        hideCompletionTimeoutRef.current = null
      }
    }
    // NOTE: We intentionally do NOT include completedChannels/initiatedChannels in deps
    // because that would cause the effect to re-run and restart the interval,
    // breaking the stateful logic with wasShowingBannerRef and completionCyclesRef.
    // The getter functions (hasChannelCompleted, getChannelCompletionMessage, etc.)
    // read from refs that are updated via useEffect in BackfillContext, so they
    // always return fresh data even though they're stable function references.
  }, [
    channel,
    channelId,
    clearChannelCompleted,
    clearChannelInitiated,
    config,
    getChannelCompletionMessage,
    hasChannelCompleted,
    hasChannelInitiated,
  ])

  return { showBanner, elapsedSeconds }
}
